import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken } from '../../../slices/staffSlice';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import useConfigOptions from '../../../hooks/useConfigOptions';
import { show } from '../../../slices/toastSlice';
import { useNavigate } from 'react-router-dom';
import { Topbar, Sidebar, Loader } from '../../../components';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon } from '@hugeicons/core-free-icons';

const inputClass = 'block w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3.5 py-2.5 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#1e3a6e]/20 focus:border-[#1e3a6e] dark:focus:border-blue-500 transition-colors';


function DeleteTransactionTab() {
    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loanFilters } = useConfigOptions();

    const [transactionId, setTransactionId] = useState('');
    const [loanId, setLoanId] = useState('');
    const [loanType, setLoanType] = useState(loanFilters[0].value);
    const [loading, setLoading] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [result, setResult] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!confirmed) { setConfirmed(true); return; }
        await executeDelete();
    };

    const executeDelete = async () => {
        setLoading(true);
        setResult(null);

        const res = await fetchRequests(
            `/loans/staff/admin/transaction`, "DELETE",
            authStaff(accessToken), {},
            { transactionId: transactionId.trim(), loanId: loanId.trim(), loanType }
        );
        setLoading(false);

        if (res?.code === 401) { sessionStorage.clear(); navigate("/login"); return; }

        if (res?.err) {
            setConfirmed(false);
            setResult({ type: 'error', message: res.err });
            dispatch(show({ type: "error", state: true, message: res.err }));
            return;
        }

        setResult({ type: 'success', message: res.message });
        setConfirmed(false);
        setTransactionId('');
        setLoanId('');
        dispatch(show({ type: "success", state: true, message: res.message }));
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-4">Delete Transaction</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Transaction ID</label>
                        <input
                            type="text"
                            value={transactionId}
                            onChange={e => { setTransactionId(e.target.value); setConfirmed(false); setResult(null); }}
                            placeholder="e.g. TXN-20250507-1234"
                            className={inputClass}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Loan ID</label>
                        <input
                            type="text"
                            value={loanId}
                            onChange={e => { setLoanId(e.target.value); setConfirmed(false); setResult(null); }}
                            placeholder="MongoDB _id of the loan"
                            className={inputClass}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Loan Type</label>
                        <select value={loanType} onChange={e => { setLoanType(e.target.value); setConfirmed(false); }} className={inputClass}>
                            {loanFilters.map(f => <option key={f.id} value={f.value}>{f.name}</option>)}
                        </select>
                    </div>

                    {confirmed && (
                        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                            <p className="text-sm font-semibold text-red-700 mb-1">Are you sure?</p>
                            <p className="text-xs text-red-600 leading-relaxed">
                                This will permanently delete transaction <span className="font-mono font-bold">{transactionId}</span> from the Ledger, PreLedger, loan application, and client profile. This cannot be undone.
                            </p>
                            <div className="flex gap-2.5 mt-3">
                                <button type="submit" disabled={loading} className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors">
                                    {loading ? <Loader color="text-white" size="w-3.5 h-3.5" /> : null}
                                    Yes, delete it
                                </button>
                                <button type="button" onClick={() => setConfirmed(false)} className="px-3.5 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {!confirmed && (
                        <button type="submit" disabled={loading || !transactionId || !loanId} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                            {loading ? <Loader color="text-white" size="w-4 h-4" /> : null}
                            Delete Transaction
                        </button>
                    )}
                </form>

                {result && (
                    <div className={`mt-4 rounded-lg p-3.5 text-sm border ${result.type === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                        {result.message}
                    </div>
                )}
            </div>

            <div className="bg-amber-50 dark:bg-amber-500/10 rounded-xl border border-amber-100 dark:border-amber-500/20 p-5">
                <h2 className="text-sm font-semibold text-amber-800 dark:text-amber-400 mb-3">What this does</h2>
                <ul className="space-y-2">
                    {[
                        { label: 'PreLedgers', desc: 'Removes the entry from PreLedgers' },
                        { label: 'Ledgers', desc: 'Removes the entry from Ledgers' },
                        { label: 'Loan Application', desc: 'Pulls the transaction from the loan\'s transactions array' },
                        { label: 'Client Profile', desc: 'Removes the payment from the client profile' },
                        { label: 'Loan Status', desc: 'If this transaction moved the loan to completed but balance is still positive, it restores the loan to active' },
                    ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm">
                            <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                            <span className="text-amber-900 dark:text-amber-300">
                                <span className="font-medium">{item.label}:</span>{' '}
                                <span className="text-amber-800 dark:text-amber-400 font-normal">{item.desc}</span>
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}


function ChangeStateTab() {
    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loanFilters } = useConfigOptions();

    const [loanId, setLoanId] = useState('');
    const [loanType, setLoanType] = useState(loanFilters[0].value);
    const [fromState, setFromState] = useState('active');
    const [toState, setToState] = useState('completed');
    const [loading, setLoading] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [result, setResult] = useState(null);

    const onFromChange = (val) => {
        setFromState(val);
        if (val === toState) setToState(val === 'active' ? 'completed' : 'active');
        setConfirmed(false);
        setResult(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!confirmed) { setConfirmed(true); return; }
        await executeChange();
    };

    const executeChange = async () => {
        setLoading(true);
        setResult(null);

        const res = await fetchRequests(
            `/loans/staff/admin/loan-state`, "PATCH",
            authStaff(accessToken), {},
            { loanId: loanId.trim(), loanType, fromState, toState }
        );
        setLoading(false);

        if (res?.code === 401) { sessionStorage.clear(); navigate("/login"); return; }

        if (res?.err) {
            setConfirmed(false);
            setResult({ type: 'error', message: res.err });
            dispatch(show({ type: "error", state: true, message: res.err }));
            return;
        }

        setResult({ type: 'success', message: res.message });
        setConfirmed(false);
        setLoanId('');
        dispatch(show({ type: "success", state: true, message: res.message }));
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-4">Change Loan State</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Loan ID</label>
                        <input
                            type="text"
                            value={loanId}
                            onChange={e => { setLoanId(e.target.value); setConfirmed(false); setResult(null); }}
                            placeholder="MongoDB _id of the loan"
                            className={inputClass}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Loan Type</label>
                        <select value={loanType} onChange={e => { setLoanType(e.target.value); setConfirmed(false); }} className={inputClass}>
                            {loanFilters.map(f => <option key={f.id} value={f.value}>{f.name}</option>)}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">From state</label>
                            <select value={fromState} onChange={e => onFromChange(e.target.value)} className={inputClass}>
                                <option value="active">Active</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">To state</label>
                            <select value={toState} onChange={e => { setToState(e.target.value); setConfirmed(false); setResult(null); }} className={inputClass}>
                                <option value="active" disabled={fromState === 'active'}>Active</option>
                                <option value="completed" disabled={fromState === 'completed'}>Completed</option>
                            </select>
                        </div>
                    </div>

                    {confirmed && (
                        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                            <p className="text-sm font-semibold text-amber-800 mb-1">Confirm state change</p>
                            <p className="text-xs text-amber-700 leading-relaxed">
                                Move loan <span className="font-mono font-bold">{loanId}</span> from <span className="font-bold">{fromState}</span> to <span className="font-bold">{toState}</span>? No transaction will be recorded — this is a direct state override.
                            </p>
                            <div className="flex gap-2.5 mt-3">
                                <button type="submit" disabled={loading} className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors">
                                    {loading ? <Loader color="text-white" size="w-3.5 h-3.5" /> : null}
                                    Yes, change state
                                </button>
                                <button type="button" onClick={() => setConfirmed(false)} className="px-3.5 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {!confirmed && (
                        <button type="submit" disabled={loading || !loanId || fromState === toState} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#162d56] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                            {loading ? <Loader color="text-white" size="w-4 h-4" /> : null}
                            Change State
                        </button>
                    )}
                </form>

                {result && (
                    <div className={`mt-4 rounded-lg p-3.5 text-sm border ${result.type === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                        {result.message}
                    </div>
                )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-500/10 rounded-xl border border-blue-100 dark:border-blue-500/20 p-5">
                <h2 className="text-sm font-semibold text-blue-800 dark:text-blue-400 mb-3">What this does</h2>
                <ul className="space-y-2">
                    {[
                        { label: 'Loan State', desc: 'Updates the loan\'s state and adminStage fields directly' },
                        { label: 'Client Record', desc: 'Syncs the change to the linked client loan status' },
                        { label: 'No transaction', desc: 'Unlike the delete-transaction tool, this records no ledger entry — use only for corrections' },
                        { label: 'Restricted', desc: 'Only transitions between active and completed are allowed' },
                        { label: 'Validation', desc: 'Rejects the change if the loan is not currently in the selected from-state' },
                    ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm">
                            <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                            <span className="text-blue-900 dark:text-blue-300">
                                <span className="font-medium">{item.label}:</span>{' '}
                                <span className="text-blue-800 dark:text-blue-400 font-normal">{item.desc}</span>
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}


function Index() {
    const tabs = ["Delete Transaction", "Change Loan State"];

    return (
        <div className="main-container">
            <Topbar />
            <Sidebar />

            <main className="main-content">
                <section className="section-container space-y-6">

                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
                            <HugeiconsIcon icon={Cancel01Icon} size={18} className="text-red-500" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Transaction Management</h1>
                            <p className="text-sm text-gray-400">Delete a transaction or directly change a loan's state</p>
                        </div>
                    </div>

                    <TabGroup>
                        <TabList className="flex border-b border-gray-200 bg-white rounded-t-xl">
                            {tabs.map(label => (
                                <Tab key={label}
                                    className={({ selected }) =>
                                        `shrink-0 px-6 py-3.5 text-sm font-medium border-b-2 -mb-px transition-colors focus:outline-none whitespace-nowrap ${
                                            selected
                                                ? 'border-[#1e3a6e] text-[#1e3a6e]'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`
                                    }>
                                    {label}
                                </Tab>
                            ))}
                        </TabList>
                        <TabPanels className="mt-6">
                            <TabPanel><DeleteTransactionTab /></TabPanel>
                            <TabPanel><ChangeStateTab /></TabPanel>
                        </TabPanels>
                    </TabGroup>

                </section>
            </main>
        </div>
    );
}

export default Index;
